import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useDisconnectMemberExternalAccount,
  useManualImportCalendarForAccount,
  useManualImportMessagesForMemberExternalAccount,
  useStartGoogleMemberExternalConnect,
  useStartMicrosoftMemberExternalConnect,
} from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import {
  GOOGLE_CALENDAR_EVENTS_SCOPE,
  GOOGLE_CALENDAR_READONLY_SCOPE,
  GOOGLE_DRIVE_READONLY_SCOPE,
  GOOGLE_GMAIL_MODIFY_SCOPE,
  MICROSOFT_CALENDAR_READWRITE_SCOPE,
  MICROSOFT_MAIL_READWRITE_SCOPE,
  MICROSOFT_MAIL_SEND_SCOPE,
  MICROSOFT_MAILBOX_SETTINGS_READWRITE_SCOPE,
  MICROSOFT_USER_READ_SCOPE,
  MemberExternalAccountSafe,
} from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { MemberExternalAccountImportSettings } from '../MemberExternalAccountImportSettings';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { startConnectInNewTab } from './start-connect-in-new-tab';

type MemberExternalAccountCardProps = {
  account: MemberExternalAccountSafe;
  memberId: number;
  onDisconnected?: () => void;
};

const PROVIDER_LABELS: Record<MemberExternalAccountSafe['provider'], string> = {
  GOOGLE: 'Google',
  MICROSOFT: 'Microsoft',
};

const SCOPE_LABELS: Record<string, string> = {
  [GOOGLE_GMAIL_MODIFY_SCOPE]: 'Gmail read, labels, drafts and send',
  [GOOGLE_CALENDAR_READONLY_SCOPE]: 'Calendar read-only',
  [GOOGLE_CALENDAR_EVENTS_SCOPE]: 'Calendar write',
  [GOOGLE_DRIVE_READONLY_SCOPE]: 'Drive read-only (calendar attachments)',
  [MICROSOFT_USER_READ_SCOPE]: 'Profile',
  [MICROSOFT_MAIL_READWRITE_SCOPE]: 'Mail read/write',
  [MICROSOFT_MAIL_SEND_SCOPE]: 'Mail send',
  [MICROSOFT_CALENDAR_READWRITE_SCOPE]: 'Calendar read/write',
  [MICROSOFT_MAILBOX_SETTINGS_READWRITE_SCOPE]: 'Outlook mailbox settings (categories)',
  openid: 'Sign-in',
  profile: 'Profile',
  email: 'Email address',
  offline_access: 'Background access',
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
}

function renderScopes(account: MemberExternalAccountSafe): string {
  if (!Array.isArray(account.scopes) || account.scopes.length === 0) {
    return 'No scopes';
  }

  return account.scopes
    .map(
      (scope) =>
        SCOPE_LABELS[scope] ??
        scope.replace('https://www.googleapis.com/auth/', '').replace('https://graph.microsoft.com/', '')
    )
    .join(', ');
}

export function MemberExternalAccountCard({ account, memberId, onDisconnected }: MemberExternalAccountCardProps) {
  const startGoogleConnect = useStartGoogleMemberExternalConnect();
  const startMicrosoftConnect = useStartMicrosoftMemberExternalConnect();
  const startConnect = account.provider === 'GOOGLE' ? startGoogleConnect : startMicrosoftConnect;
  const disconnect = useDisconnectMemberExternalAccount(memberId);
  const messageImport = useManualImportMessagesForMemberExternalAccount(memberId);
  const calendarImport = useManualImportCalendarForAccount(memberId);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const label = PROVIDER_LABELS[account.provider];
  const isMessageImportActive = messageImport.isPending;
  const isCalendarImportActive = calendarImport.isPending;

  const handleReconnect = () => {
    startConnectInNewTab({ label, start: () => startConnect.mutateAsync() });
  };

  const handleDisconnect = () => {
    disconnect.mutate(account.id, {
      onSuccess: () => {
        toast.success('Disconnected account.');
        onDisconnected?.();
      },
      onError: (error: unknown) => {
        toast.error(getAxiosErrorDisplayMessage(error, 'Failed to disconnect account.'));
      },
    });
  };

  const handleManualMessageImport = () => {
    messageImport.mutate(
      { id: account.id, input: {} },
      {
        onSuccess: (result) => {
          toast.success(`Imported ${result.importedCount} message${result.importedCount === 1 ? '' : 's'}.`);
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to import mail.'));
        },
      }
    );
  };

  const handleManualCalendarImport = () => {
    calendarImport.mutate(
      { id: account.id, input: {} },
      {
        onSuccess: (result) => {
          toast.success(`Imported ${result.importedCount} event${result.importedCount === 1 ? '' : 's'}.`);
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to import calendar.'));
        },
      }
    );
  };

  return (
    <div className="rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="font-medium">{account.externalEmail}</p>
            <Badge variant="outline">{label}</Badge>
            <Badge variant={account.isActive ? 'default' : 'secondary'}>
              {account.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {account.messageSync.needsReauth ? (
              <Badge variant="destructive" title={account.messageSync.needsReauthReason ?? undefined}>
                Reconnect needed
              </Badge>
            ) : null}
          </div>
          {!account.messageSync.needsReauth && account.messageSync.lastCompletedAt ? (
            <p className="text-xs text-muted-foreground">
              Last synced {formatDateTime(account.messageSync.lastCompletedAt)}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">Scopes: {renderScopes(account)}</p>
          <p className="text-xs text-muted-foreground">
            Mailbox labels: {account.labelWriteCapable ? 'available' : 'reconnect to enable'}
          </p>
          <p className="text-xs text-muted-foreground">Token expiry: {formatDateTime(account.tokenExpiry)}</p>
          <p className="text-xs text-muted-foreground">Updated: {formatDateTime(account.updatedAt)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disconnect.isPending}
            onClick={() => setDisconnectOpen(true)}
          >
            Disconnect
          </Button>
          {!account.isActive ? (
            <Button type="button" size="sm" onClick={handleReconnect} disabled={startConnect.isPending || memberId <= 0}>
              Reconnect
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isMessageImportActive || !account.isActive}
            onClick={handleManualMessageImport}
            aria-busy={isMessageImportActive}
            aria-label={isMessageImportActive ? 'Importing mail…' : 'Import mail'}
            className="relative"
          >
            <span className={isMessageImportActive ? 'invisible' : undefined} aria-hidden={isMessageImportActive}>
              Import mail
            </span>
            {isMessageImportActive ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : null}
          </Button>
          {account.calendarImportCapable ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isCalendarImportActive || !account.isActive}
              onClick={handleManualCalendarImport}
              aria-busy={isCalendarImportActive}
              aria-label={isCalendarImportActive ? 'Importing Calendar…' : 'Import Calendar'}
              className="relative"
            >
              <span className={isCalendarImportActive ? 'invisible' : undefined} aria-hidden={isCalendarImportActive}>
                Import Calendar
              </span>
              {isCalendarImportActive ? (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          ) : null}
        </div>
      </div>
      <MemberExternalAccountImportSettings account={account} memberId={memberId} />

      <ConfirmActionDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect account"
        description="Disconnect this account?"
        confirmLabel="Disconnect"
        onConfirm={handleDisconnect}
      />
    </div>
  );
}
